/**
 * "This document is the seller's Direct Sale preview."
 *
 * Two ways in, one treatment: the frame on the seller's shop-preview page,
 * and the shared test link (pages/TryDirectSalePage.tsx), which opens the
 * configurator full-window — often in the very browser the seller works in.
 *
 * The preview (pages/ShopPreviewPage.tsx) embeds /direct-sale in an iframe
 * of the seller's own area — same origin, same tab. Left alone, the frame
 * would share everything the seller's app keeps in the browser:
 *
 * - the persisted stores (canvas state, product selection): designing in
 *   the preview would overwrite the working state of the seller's Creator;
 * - the seller's tokens: a 401 inside the frame — an entry link that has
 *   expired, say — would start a token refresh with the seller's refresh
 *   token, race the app around it and could log the seller out.
 *
 * So a preview frame gets a storage area of its own and no tokens at all.
 * Direct Sale needs neither: its customers are anonymous and carry their
 * own session header.
 *
 * Decided once per document, from the URL the frame was loaded with: later
 * in-app navigation may drop the query, the document stays a preview.
 */
export const PREVIEW_FRAME_PARAM = 'preview';
const STORAGE_PREFIX = 'designdeck.preview.';

/** What is asked of a window — enough for a test to hand in. */
interface WindowLike {
  location: { pathname: string; search: string };
}

/**
 * Exported for tests; the app asks isPreviewFrame().
 *
 * Framed or not: the marker only ever applies to /direct-sale, a route that
 * needs no seller token, so switching it on by hand hides nothing that
 * document would use. A real shop's embed never carries it.
 */
export function detectPreviewFrame(win: WindowLike = window): boolean {
  return (
    win.location.pathname.startsWith('/direct-sale') &&
    new URLSearchParams(win.location.search).get(PREVIEW_FRAME_PARAM) === '1'
  );
}

const PREVIEW_FRAME = detectPreviewFrame();

export function isPreviewFrame(): boolean {
  return PREVIEW_FRAME;
}

/**
 * sessionStorage under a prefix. sessionStorage alone would not do: a
 * same-origin frame shares it with its tab, and an impersonation tab keeps
 * the seller's state exactly there.
 */
export const previewStorage: Pick<
  Storage,
  'getItem' | 'setItem' | 'removeItem'
> = {
  getItem: (name) => window.sessionStorage.getItem(STORAGE_PREFIX + name),
  setItem: (name, value) =>
    window.sessionStorage.setItem(STORAGE_PREFIX + name, value),
  removeItem: (name) => window.sessionStorage.removeItem(STORAGE_PREFIX + name),
};
